import React, { useEffect, useState } from "react";
import { loadState, saveState } from "../../utils/localStorage.utils";
import { LocalStorageKeys } from "../../utils/strings.utils";
import "./index.css";
import { useViewport } from "../../hooks/useViewport";
import { useWhiteboardRoom } from "../../hooks/useWhiteboardRoom";
import api from "../../utils/api";
import { getFeatures, getSecrets } from "../../config";
import ClearIcon from "@mui/icons-material/Clear";

interface PWAIcon {
  file_name: string;
}
interface PWASetting {
  id: number;
  domain: string;
  is_pwa_allowed: boolean;
  title: string;
  icon: PWAIcon | null;
}

const DISMISS_INTERVAL = 7 * 24 * 60 * 60 * 1000; // 7 天（毫秒）

const PWAInstallSheet = () => {
  const [showSheet, setShowSheet] = useState(false);
  const isInIframe = window.self !== window.top;
  const { isAppOpened } = useViewport();
  const { roomDomain } = useWhiteboardRoom();
  const [pwaSetting, setPWASetting] = useState<PWASetting | null>(null);
  const ASSETS_BASE_URL = "https://cdn.protico.io";
  const { DAPP_DOMAIN } = getSecrets();
  const ASSETS_ENV_PREFIX = DAPP_DOMAIN === "main.protico.io" ? "" : "/dev";
  const { FEATURE_PWA_ENABLED } = getFeatures();
  const getDomainPWASettings = async (domain: string) => {
    try {
      const { data } = await api.get<PWASetting>(`/api/domain/${domain}/pwa`);
      return data;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  // 返回 timer id
  const getSettingsAndSetSheet = async (domain: string) => {
    const data = await getDomainPWASettings(domain);
    if (data) {
      setPWASetting(data);
      if (data.is_pwa_allowed) {
        const id = setTimeout(() => setShowSheet(true), 3000);
        return id;
      }
    }
    return undefined;
  };

  // —— iframe 内：计数 & 触发 sheet —— //
  useEffect(() => {
    if (!FEATURE_PWA_ENABLED) return;
    if (!isInIframe || !isAppOpened || !roomDomain) return;

    const prev = loadState(LocalStorageKeys.APP_VISIT_COUNT) ?? 0;
    const next = prev + 1;
    saveState(LocalStorageKeys.APP_VISIT_COUNT, next);

    // 检查上次拒绝时间
    const lastDismiss = loadState(LocalStorageKeys.PWA_DISMISS_DATE);
    if (lastDismiss && Date.now() - lastDismiss < DISMISS_INTERVAL) {
      return; // 7 天内拒绝过，跳过弹窗
    }

    let timerId: ReturnType<typeof setTimeout> | undefined;
    if (next >= 2) {
      getSettingsAndSetSheet(roomDomain).then((id) => {
        timerId = id;
      });
    }

    return () => {
      if (timerId !== undefined) clearTimeout(timerId);
    };
  }, [isInIframe, isAppOpened, roomDomain]);

  // —— iframe 外：拦截 beforeinstallprompt —— //
  useEffect(() => {
    if (isInIframe) return;

    const handler = (e: Event & { prompt: () => void }) => {
      e.preventDefault();
      e.prompt();
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [isInIframe]);

  const handleInstallClick = () => {
    window.open(window.location.href, "_blank");
    setShowSheet(false);
  };

  const handleInstallCancel = () => {
    // 记录拒绝时间，下次 7 天后才会再弹
    saveState(LocalStorageKeys.PWA_DISMISS_DATE, Date.now());
    setShowSheet(false);
  };

  if (!isInIframe) return null;

  return (
    !!pwaSetting &&
    pwaSetting.is_pwa_allowed && (
      <div className={`install-sheet ${showSheet ? "visible" : ""}`}>
        <button
          className="install-sheet__cancel-btn"
          onClick={handleInstallCancel}
        >
          <ClearIcon />
        </button>
        <div className="install-sheet__left">
          <img
            src={
              pwaSetting.icon?.file_name
                ? `${ASSETS_BASE_URL}/196x196/webp/pwa_icon${ASSETS_ENV_PREFIX}/${pwaSetting.domain}/${pwaSetting.icon.file_name}`
                : `${ASSETS_BASE_URL}/196x196/webp/pwa_icon/favicon.png`
            }
            alt="App Icon"
            className="install-sheet__icon"
          />
          <div className="install-sheet__text">
            <span className="install-sheet__title">
              {pwaSetting.title ? `安装 ${pwaSetting.title}` : `安装 Protico`}
            </span>
            <span className="install-sheet__subtitle">在手機上開始聊天</span>
          </div>
        </div>
        <button className="install-sheet__btn" onClick={handleInstallClick}>
          開啟新分頁
        </button>
      </div>
    )
  );
};

export default PWAInstallSheet;
